import React, { useState } from "react";
import { useDarkMode } from "../../context/DarkModeContext";
import { Search, BookOpen, Clock, User, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  author: string;
  date: string;
  content: string;
}

const Articles: React.FC = () => {
  const { isDark } = useDarkMode();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const navigate = useNavigate();

  const articles: Article[] = [
    {
      id: "premium-vs-free",
      title: "Premium vs Free: Feature Comparison",
      description:
        "Understand the key differences between our free and premium plans to make the best choice for your job search.",
      category: "plans",
      readTime: "5 min read",
      author: "Find-Work Team",
      date: "2024-01-15",
      content: "Detailed comparison of features...",
    },
    {
      id: "optimize-profile",
      title: "How to Optimize Your Profile for Better Job Matches",
      description:
        "Learn proven strategies to enhance your profile and increase your chances of finding the perfect job match.",
      category: "profile",
      readTime: "8 min read",
      author: "Career Experts",
      date: "2024-01-12",
      content: "Profile optimization tips...",
    },
    {
      id: "understanding-our-job-matching-algorithm",
      title: "Understanding Our Job Matching Algorithm",
      description:
        "Get insights into how our algorithm matches your skills and preferences with the right job opportunities.",
      category: "technology",
      readTime: "6 min read",
      author: "Tech Team",
      date: "2024-01-10",
      content: "Algorithm explanation...",
    },
    {
      id: "managing-your-application-pipeline",
      title: "Managing Your Application Pipeline",
      description:
        "Effective strategies to organize and track your job applications for better results.",
      category: "applications",
      readTime: "7 min read",
      author: "Career Coaches",
      date: "2024-01-08",
      content: "Pipeline management tips...",
    },
    {
      id: "how-to-set-up-effective-job-alerts",
      title: "How to Set Up Effective Job Alerts",
      description:
        "Create smart job alerts that deliver relevant opportunities directly to your inbox.",
      category: "tools",
      readTime: "4 min read",
      author: "Product Team",
      date: "2024-01-05",
      content: "Job alert setup guide...",
    },
    {
      id: "resume-tips-that-get-you-noticed",
      title: "Resume Tips That Get You Noticed",
      description:
        "Professional resume writing tips that will make your application stand out to employers.",
      category: "resume",
      readTime: "10 min read",
      author: "HR Professionals",
      date: "2024-01-03",
      content: "Resume writing guide...",
    },
  ];

  const categories = [
    { id: "all", name: "All Articles", count: articles.length },
    {
      id: "plans",
      name: "Plans & Pricing",
      count: articles.filter((a) => a.category === "plans").length,
    },
    {
      id: "profile",
      name: "Profile Optimization",
      count: articles.filter((a) => a.category === "profile").length,
    },
    {
      id: "technology",
      name: "Technology",
      count: articles.filter((a) => a.category === "technology").length,
    },
    {
      id: "applications",
      name: "Applications",
      count: articles.filter((a) => a.category === "applications").length,
    },
    {
      id: "tools",
      name: "Tools & Features",
      count: articles.filter((a) => a.category === "tools").length,
    },
    {
      id: "resume",
      name: "Resume Tips",
      count: articles.filter((a) => a.category === "resume").length,
    },
  ];

  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleArticleClick = (articleId: string) => {
    navigate(`/articles/${articleId}`);
  };

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"} transition-colors duration-200`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="relative inline-block mb-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-sky-400 to-blue-500 text-white mb-4">
              <BookOpen className="h-12 w-12" />
            </div>
            <div className="absolute -top-1 -right-1">
              <TrendingUp className="h-5 w-5 text-green-400" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
            Help Center & Articles
          </h1>
          <p
            className={`text-xl max-w-2xl mx-auto leading-relaxed ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Expert advice and tips to help you succeed in your job search
            journey
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                isDark
                  ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                selectedCategory === category.id
                  ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-105"
                  : isDark
                    ? "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white shadow-md"
                    : "bg-white text-gray-600 hover:bg-sky-50 hover:text-sky-700 border border-gray-200 shadow-sm"
              }`}
            >
              {category.name}
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  selectedCategory === category.id
                    ? "bg-white text-sky-600"
                    : isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              isDark={isDark}
              onClick={() => handleArticleClick(article.id)}
            />
          ))}
        </div>

        {/* Empty State */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4 opacity-50" />
            <h3
              className={`text-xl font-semibold mb-3 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              No articles found
            </h3>
            <p className={isDark ? "text-gray-400" : "text-gray-500"}>
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("all");
              }}
              className={`mt-4 px-4 py-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-gray-800 text-sky-300 hover:bg-gray-700"
                  : "bg-white text-sky-600 hover:bg-sky-50 border border-sky-200"
              }`}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Article Card Component
interface ArticleCardProps {
  article: Article;
  isDark: boolean;
  onClick: () => void;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  isDark,
  onClick,
}) => {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-xl border transition-all duration-300 hover:scale-105 group ${
        isDark
          ? "bg-gray-800 border-gray-700 hover:border-sky-500 hover:shadow-lg hover:shadow-sky-500/20"
          : "bg-white border-gray-200 hover:border-sky-400 hover:shadow-lg hover:shadow-sky-200"
      }`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <span
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
              isDark
                ? "bg-gray-700 text-gray-300 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-sky-500 group-hover:to-blue-600"
                : "bg-gray-100 text-gray-600 group-hover:text-white group-hover:bg-gradient-to-r group-hover:from-sky-400 group-hover:to-blue-500"
            }`}
          >
            {article.category.charAt(0).toUpperCase() +
              article.category.slice(1)}
          </span>
          <div
            className={`flex items-center text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            <Clock className="h-3 w-3 mr-1" />
            {article.readTime}
          </div>
        </div>

        <h3
          className={`font-bold text-lg mb-3 leading-tight transition-colors group-hover:bg-gradient-to-r group-hover:from-sky-500 group-hover:to-blue-600 group-hover:bg-clip-text group-hover:text-transparent ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {article.title}
        </h3>

        <p
          className={`text-sm mb-4 line-clamp-3 leading-relaxed ${
            isDark ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {article.description}
        </p>

        <div
          className={`flex items-center justify-between text-xs pt-3 border-t ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div
            className={`flex items-center ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            <User className="h-3 w-3 mr-1" />
            {article.author}
          </div>
          <span className={isDark ? "text-gray-400" : "text-gray-500"}>
            {new Date(article.date).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Articles;
